import { all, takeLatest, put, call, select } from "redux-saga/effects";
import { toast } from 'react-hot-toast';
import organizationAddEditSlice, { createOrganization, loadOrganization, loadUserGroups, loadEnterprises, updateOrganization, loadUserGroupTestPrices, loadAccountReps, loadOrderStats, selectOrganizationAddEditIsBusy, selectOrganization } from "./slice";
import { IConfiguration } from "../../../shared/types";
import { SimplyTestApi } from "../../../api/simplytest";
import { selectConfig } from "../../../shared/redux/slice";
import { selectIdToken, unauthorized } from "../../../auth/redux/slice";
import { AxiosResponse } from "axios";
import { OrganizationItem, UserGroup, UserGroupTestPrice, AccountRep, AccountRepsResponse, OrderStats, Enterprise } from "../../../api/types";
import { generatePath } from "react-router-dom";
import { ROUTES } from "../../../shared/constants";

export abstract class OrganizationAddEditSagas {
    public static* run() {
        try {
            yield all([
                takeLatest(loadOrganization, OrganizationAddEditSagas.loadOrganization),
                takeLatest(loadOrderStats, OrganizationAddEditSagas.loadOrderStats),
                takeLatest(loadAccountReps, OrganizationAddEditSagas.loadAccountReps),
                takeLatest(createOrganization, OrganizationAddEditSagas.createOrganization),
                takeLatest(updateOrganization, OrganizationAddEditSagas.updateOrganization),
                takeLatest(loadUserGroups, OrganizationAddEditSagas.loadUserGroups),
                takeLatest(loadEnterprises, OrganizationAddEditSagas.loadEnterprises),
                takeLatest(loadUserGroupTestPrices, OrganizationAddEditSagas.loadUserGroupTestPrices),
            ]);
        }
        catch(e: any){
            toast.error('Unable to register OrganizationAddEdit module.');
            console.log(`[OrganizationAddEditSagas:run] the following exception has occurred`, e);
        }
    }
    
    public static* loadAccountReps(action: ReturnType<typeof loadAccountReps>){
        const isLoading: boolean = yield select(selectOrganizationAddEditIsBusy);
        try {
            if(!isLoading)
                yield put(organizationAddEditSlice.actions.setState('BUSY'));
            
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const accountRepsResponse: AxiosResponse<AccountRep[]> = yield call(
                {
                    context: client,
                    fn: client.allAccountReps
                },
            );

            if(accountRepsResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(accountRepsResponse.status !== 200)
                throw new Error("Error communicating with Api");

            // console.log(`[OrganizationAddEditSagas:loadAccountReps data] `, accountRepsResponse.data);

            yield put(organizationAddEditSlice.actions.setAccountReps(accountRepsResponse.data));
            if(isLoading)
                yield put(organizationAddEditSlice.actions.setState('BUSY'));
        }
        catch(e: any){
            toast.error('Unable to load the account reps.');
            // console.log(`[OrganizationAddEditSagas:loadAccountReps] the following exception has occurred`, e);
            if(isLoading)
                yield put(organizationAddEditSlice.actions.setState('READY'));
        }
    }

    public static* loadUserGroups(_: ReturnType<typeof loadUserGroups>){
        try {
            yield put(organizationAddEditSlice.actions.setState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const userGroupsResponse: AxiosResponse<UserGroup[]> = yield call(
                {
                    context: client,
                    fn: client.getUserGroups,
                }
            );

            if(userGroupsResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }
            
            if(userGroupsResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(organizationAddEditSlice.actions.setUserGroups(userGroupsResponse.data));
            yield put(organizationAddEditSlice.actions.setState('READY'));
        }
        catch(e: any){
            toast.error('Unable to load price groups.');
            // console.log(`[OrganizationAddEditSagas:loadUserGroups] the following exception has occurred`, e);
        }
    }

    // Enterprise/billing feature - only ever dispatched from the Portal-Admin-gated section of
    // OrganizationForm.tsx, since GET /api/enterprises itself is Portal-Admin-only server-side;
    // a non-admin dispatching this would just get a 403 and a spurious error toast for a dropdown
    // they'd never see anyway.
    public static* loadEnterprises(_: ReturnType<typeof loadEnterprises>){
        try {
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const enterprisesResponse: AxiosResponse<Enterprise[]> = yield call(
                {
                    context: client,
                    fn: client.getEnterprises,
                }
            );

            if(enterprisesResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(enterprisesResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(organizationAddEditSlice.actions.setEnterprises(enterprisesResponse.data));
        }
        catch(e: any){
            console.log(`[OrganizationAddEditSagas:loadEnterprises] the following exception has occurred`, e);
        }
    }

    public static* loadUserGroupTestPrices(action: ReturnType<typeof loadUserGroupTestPrices>){
        const { payload: { userGroupId }} = action;
        try {
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const userGroupTestPriceResponse: AxiosResponse<UserGroupTestPrice> = yield call(
                {
                    context: client,
                    fn: client.getUserGroupTestPrices,
                },
		userGroupId,
            );

            if(userGroupTestPriceResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }
            
            if(userGroupTestPriceResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(organizationAddEditSlice.actions.setUserGroupTestPrices(userGroupTestPriceResponse.data));
            yield put(organizationAddEditSlice.actions.setState('READY'));
        }
        catch(e: any){
            toast.error('Unable to load price groups test prices.');
            console.log(`[OrganizationAddEditSagas:loadUserGroupTestPrices] the following exception has occurred`, e);
        }
    }
    
    public static* loadOrganization(action: ReturnType<typeof loadOrganization>){
        const { payload: { orgId }} = action;
        try {
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const loadOrgResponse: AxiosResponse<OrganizationItem> = yield call(
                {
                    context: client,
                    fn: client.getOrganization,
                },
                orgId,
            );

            if(loadOrgResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(loadOrgResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(organizationAddEditSlice.actions.setOrganization(loadOrgResponse.data));
        }
        catch(e: any){
            toast.error('Unable to load organization.');
            console.log(`[OrganizationAddEditSagas:loadOrganization] the following exception has occurred`, e);
        }
    }

    public static* loadOrderStats(action: ReturnType<typeof loadOrderStats>){
        const { payload: { orgId }} = action;
        try {
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const orderStatsResponse: AxiosResponse<OrderStats> = yield call(
                {
                    context: client,
                    fn: client.getOrganizationOrderStats,
                },
                orgId,
            );

            if(orderStatsResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(orderStatsResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(organizationAddEditSlice.actions.setOrderStats(orderStatsResponse.data));
        }
        catch(e: any){
            toast.error('Unable to load order stats.');
            console.log(`[OrganizationAddEditSagas:loadOrderStats] the following exception has occurred`, e);
        }
    }

    public static* createOrganization(action: ReturnType<typeof createOrganization>){
        const { payload: { organization, navigate }} = action;
        try {
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const newOrgResponse: AxiosResponse<OrganizationItem> = yield call(
                {
                    context: client,
                    fn: client.createOrganization,
                },
                organization,
            );

            if(newOrgResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(newOrgResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield call({ context: toast, fn: toast.success}, 'Organization Created.');
            // Land on the new org's Edit page rather than bouncing back to the Organizations list -
            // keeps the admin in context on the record they just created (also means the Save
            // button's dirty-tracking is now against the real saved values, not "still creating").
            // Guarded separately from the try block above: the org is already created and the
            // success toast already shown at this point, so a navigation problem here must never
            // throw back into the catch below - that previously fired a false "Unable to create
            // organization" error toast right alongside the real success one.
            if (newOrgResponse.data?.orgId != null) {
                navigate(generatePath(ROUTES.EditOrganization, { orgId: newOrgResponse.data.orgId }));
            } else {
                console.error('[OrganizationAddEditSagas:createOrganization] response had no orgId, falling back to the Organizations list', newOrgResponse.data);
                navigate(generatePath(ROUTES.Organizations));
            }
        }
        catch(e: any){
            toast.error('Unable to create organization.');
            console.log(`[OrganizationAddEditSagas:createOrganization] the following exception has occurred`, e);
        }
    }

    public static* updateOrganization(action: ReturnType<typeof updateOrganization>){
        const { payload: { orgId, organization }} = action;
        try {
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const updateOrgResponse: AxiosResponse<OrganizationItem> = yield call(
                {
                    context: client,
                    fn: client.updateOrganization,
                },
                orgId,
                organization
            );

            if(updateOrgResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(updateOrgResponse.status !== 204)
                throw new Error("Error communicating with Api");

            // Stay on the Edit Organization page after saving - previously navigated back to the
            // Dashboard, which was a jarring "where did my changes go" surprise for the admin. The
            // update endpoint returns 204 (no body), so re-baseline the form's dirty-tracking from
            // the submitted (now-confirmed-saved) values ourselves rather than a real reload -
            // this is what lets OrganizationForm's Save button go back to disabled after a save.
            const currentOrg = yield select(selectOrganization);
            yield put(organizationAddEditSlice.actions.setOrganization({ ...currentOrg, ...organization, orgId: Number(orgId) }));
            yield call({ context: toast, fn: toast.success}, `Organization #${orgId} updated.`);
        }
        catch(e: any){
            toast.error('Unable to update organization.');
            console.log(`[OrganizationAddEditSagas:updateOrganization] the following exception has occurred`, e);
        }
    }
}
